import type { AppNotification } from '@/net/api';

/**
 * The home-screen badge and the notification tray, kept to ONE number: the server's unread count.
 *
 * Three things speak for "you have news" on a phone: the avatar's dot in the app, the icon's badge
 * (the Badging API; `public/sw.js` sets it from the count each push carries), and the system tray
 * (on Android the launcher dot/number counts the tray's notifications). They agree when the badge is
 * always `unread` as the server last said it, and a tray entry lives only while its row is unread.
 * This module is the pure half of that (so it can be tested); `net/notify.ts` runs it.
 */

/** The slice of `navigator` the Badging API lives on. */
export interface BadgeNav {
  setAppBadge?: (count?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
}

/** A badge number: a whole count, never negative. */
export function clampBadge(n: number): number {
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

export type BadgeResult = 'set' | 'cleared' | 'unsupported';

/** Set the icon badge to `unread` (cleared at zero). Never throws: a browser without the API does nothing. */
export async function syncAppBadge(unread: number, nav: BadgeNav): Promise<BadgeResult> {
  const n = clampBadge(unread);
  try {
    if (n > 0 && typeof nav.setAppBadge === 'function') {
      await nav.setAppBadge(n);
      return 'set';
    }
    if (typeof nav.clearAppBadge === 'function') {
      await nav.clearAppBadge();
      return 'cleared';
    }
    if (n === 0 && typeof nav.setAppBadge === 'function') {
      // The older spelling: a count of zero clears.
      await nav.setAppBadge(0);
      return 'cleared';
    }
  } catch {
    // A refused badge (permission, an uninstalled app) is not an error worth showing.
  }
  return 'unsupported';
}

/** The tag the server gives a row's push (server/src/push.ts), so the tray can be matched to rows. */
export function trayTagFor(item: Pick<AppNotification, 'challengeId' | 'chestId'>): string {
  return item.chestId ? `chest-${item.chestId}` : `challenge-${item.challengeId ?? ''}`;
}

/**
 * Which of the tray's notifications (by tag) should be closed: those whose rows are all read. With
 * nothing unread at all the whole tray goes. A tag with no row in the loaded inbox (older than the
 * 20 newest) is left alone while anything is unread: the count cannot say it is read.
 */
export function staleTrayTags(
  tags: readonly string[],
  items: readonly AppNotification[],
  unread: number,
): string[] {
  if (clampBadge(unread) === 0) return [...tags];
  const known = new Set<string>();
  const live = new Set<string>();
  for (const item of items) {
    const tag = trayTagFor(item);
    known.add(tag);
    if (item.readAt === null) live.add(tag);
  }
  return tags.filter((t) => known.has(t) && !live.has(t));
}

/** The minimum gap between two foreground resyncs (a focus, a visibility change and a pageshow arrive together). */
export const RESYNC_GAP_MS = 4_000;

/** Whether a resync asked for at `now` should run, given when the last one ran (0 = never). */
export function shouldResync(lastAt: number, now: number, gap = RESYNC_GAP_MS): boolean {
  return lastAt === 0 || now - lastAt >= gap;
}

/** The slice of a service worker registration the tray sweep uses. */
export interface TrayRegistration {
  getNotifications: () => Promise<{ tag: string; close: () => void }[]>;
}

/** Close the tray notifications that no longer have an unread row. Returns how many it closed. */
export async function sweepTray(
  reg: TrayRegistration | null | undefined,
  items: readonly AppNotification[],
  unread: number,
): Promise<number> {
  if (!reg) return 0;
  try {
    const shown = await reg.getNotifications();
    const stale = new Set(
      staleTrayTags(
        shown.map((n) => n.tag),
        items,
        unread,
      ),
    );
    let closed = 0;
    for (const n of shown)
      if (stale.has(n.tag)) {
        n.close();
        closed++;
      }
    return closed;
  } catch {
    return 0;
  }
}

/**
 * What "the reader has seen the list" marks read: the rows on screen by id, or EVERYTHING when the
 * server counts more unread rows than the list shows (the inbox keeps the 20 newest; an older unread
 * row has no place on screen to be read from and would keep the dot and the icon badge lit forever).
 * Null: nothing to mark.
 */
export function seenPlan(pendingIds: readonly string[], unread: number): 'all' | string[] | null {
  if (clampBadge(unread) > pendingIds.length) return 'all';
  return pendingIds.length > 0 ? [...pendingIds] : null;
}
